import { electronInstructionsApi } from './ElectronInstructionsApi';
import type { CachedInstructionMapping } from '../instructionsTypes';

export interface NoticeResolutionResult {
  resolvedPaths: CachedInstructionMapping;
  missingUrls: string[];
  downloadedUrls: string[];
}

const uniqueUrls = (urls: string[]): string[] => Array.from(new Set(urls.filter(Boolean)));

export class NoticePathResolver {
  constructor(private readonly instructionsBridge = electronInstructionsApi) {}

  private normalizeKey(input: string): string {
    // Trim and convert URLs to pathname-based keys to tolerate absolute vs relative differences
    const value = (input || '').trim();
    if (!value) return '';
    try {
      if (/^https?:\/\//i.test(value)) {
        const url = new URL(value);
        return this.stripLocalePrefix(url.pathname || value);
      }
      const p = value.startsWith('/') ? value : `/${value}`;
      return this.stripLocalePrefix(p);
    } catch {
      return value;
    }
  }

  private stripLocalePrefix(pathname: string): string {
    // Collapse locale prefixes like /fr/, /nl/, /en/ to be locale-agnostic
    return pathname.replace(/^\/(fr|nl|en)(?=\/|$)/i, '');
  }

  private toAbsolute(url: string): string {
    const trimmed = (url || '').trim();
    if (!trimmed) return '';
    if (/^https?:\/\//i.test(trimmed)) return trimmed;
    const base = (import.meta as any)?.env?.VITE_API_URL || '';
    const path = trimmed.replace(/^\/+/, '');
    return base ? `${base}/${path}` : `/${path}`;
  }

  async resolvePaths(
    partOrderId: number,
    instructionUrls: string[],
  ): Promise<NoticeResolutionResult> {
    // Trim duplicates and whitespace
    const normalizedUrls = uniqueUrls(instructionUrls.map(u => (u ?? '').trim()).filter(u => u.length > 0));

    if (normalizedUrls.length === 0) {
      return { resolvedPaths: {}, missingUrls: [], downloadedUrls: [] };
    }

    try {
      const isAvailable = await this.instructionsBridge.isAvailable();
      if (!isAvailable) {
        return {
          resolvedPaths: {},
          missingUrls: normalizedUrls,
          downloadedUrls: [],
        };
      }

      const resolved: CachedInstructionMapping = {};
      const downloadedUrls: string[] = [];

      const cachedPaths = await this.safeGetLocalPaths(partOrderId);
      // Build a secondary index tolerant to absolute/relative differences
      const cachedByNormalizedKey: Record<string, string> = {};
      for (const [key, filePath] of Object.entries(cachedPaths)) {
        cachedByNormalizedKey[this.normalizeKey(key)] = filePath;
      }

      const missingUrls: string[] = [];

      for (const url of normalizedUrls) {
        if (cachedPaths[url]) {
          resolved[url] = cachedPaths[url];
          continue;
        }
        const alt = cachedByNormalizedKey[this.normalizeKey(url)];
        if (alt) {
          resolved[url] = alt;
          continue;
        }
        missingUrls.push(url);
      }

      if (missingUrls.length > 0) {
        // Prefer absolute URLs when asking Electron to download/cache
        const absoluteUrls = missingUrls.map(u => this.toAbsolute(u));
        const downloaded = await this.safeDownload(absoluteUrls);

        // Create a normalized index for the downloaded mapping as well
        const downloadedByNormalizedKey: Record<string, string> = {};
        for (const [key, filePath] of Object.entries(downloaded)) {
          downloadedByNormalizedKey[this.normalizeKey(key)] = filePath;
        }

        for (let i = 0; i < missingUrls.length; i++) {
          const original = missingUrls[i];
          const absolute = absoluteUrls[i];
          if (downloaded[original]) {
            resolved[original] = downloaded[original];
            downloadedUrls.push(original);
            continue;
          }
          if (downloaded[absolute]) {
            resolved[original] = downloaded[absolute];
            downloadedUrls.push(original);
            continue;
          }
          const byNorm = downloadedByNormalizedKey[this.normalizeKey(original)]
            || downloadedByNormalizedKey[this.normalizeKey(absolute)];
          if (byNorm) {
            resolved[original] = byNorm;
            downloadedUrls.push(original);
          }
        }

        const stillMissing = missingUrls.filter(url => !resolved[url]);
        return {
          resolvedPaths: resolved,
          missingUrls: stillMissing,
          downloadedUrls,
        };
      }

      return {
        resolvedPaths: resolved,
        missingUrls: [],
        downloadedUrls,
      };
    } catch (error) {
      console.error('Failed to resolve instruction paths:', error);
      return {
        resolvedPaths: {},
        missingUrls: normalizedUrls,
        downloadedUrls: [],
      };
    }
  }

  private async safeGetLocalPaths(partOrderId: number): Promise<CachedInstructionMapping> {
    try {
      return await this.instructionsBridge.getLocalPaths(partOrderId);
    } catch (error) {
      console.error('Error retrieving cached instruction paths:', error);
      return {};
    }
  }

  private async safeDownload(urls: string[]): Promise<CachedInstructionMapping> {
    try {
      return await this.instructionsBridge.downloadAndCache(urls);
    } catch (error) {
      console.error('Error downloading missing instruction paths:', error);
      return {};
    }
  }
}

const resolver = new NoticePathResolver();

export default resolver;
